import { useEffect, useMemo, useRef, useState } from "react";
import {
  Link,
  useFetcher,
  useRevalidator,
  useRouteLoaderData,
} from "react-router";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ChevronDown, Loader2, Lock, PlayCircle, Plus, Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { getAttackMethods, type AttackMethod } from "@/lib/adversaryAPI";
import {
  type SpecificationsActionResult,
  type SpecificationsLoaderData,
  type SpecificationRunSummary,
} from "@/routes/specificationsRoute";
import {
  WORKSPACE_LOADER_ID,
  type WorkspaceLoaderData,
} from "@/routes/workspaceLoader";
import type { Database } from "@/integrations/supabase/types";

const formatAttackMethodLabel = (value: string) =>
  value
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

const getDatasetKindLabel = (
  kind: Database["public"]["Enums"]["dataset_kind"] | null | undefined,
) => (kind === "operational_rules" ? "Rules" : "Behavior");

const formatRunStatus = (status: string | null | undefined) =>
  status ? status.replace(/^[a-z]/, (match) => match.toUpperCase()) : "Unknown";

const formatRunTimestamp = (run: SpecificationRunSummary | null) =>
  run?.started_at
    ? formatDistanceToNow(new Date(run.started_at), { addSuffix: true })
    : null;

const formatRunProgress = (run: SpecificationRunSummary | null) => {
  if (!run) return null;

  const totalSteps = run.total_steps;
  if (!totalSteps || totalSteps <= 0) return null;

  if (run.status === "Complete") {
    return `Step ${totalSteps}/${totalSteps}`;
  }

  const stepNumber =
    run.latest_step_number ??
    (typeof run.completed_steps === "number"
      ? Math.min(run.completed_steps + 1, totalSteps)
      : null);

  if (!stepNumber) return null;

  if (run.latest_step_slug) {
    return `Step ${stepNumber}/${totalSteps} · ${run.latest_step_slug}`;
  }

  return `Step ${stepNumber}/${totalSteps}`;
};

const getStatusBadgeVariant = (status: string) => {
  if (status === "ready") return "default";
  if (status === "failed") return "destructive";
  return "secondary" as const;
};

interface SpecificationsViewProps {
  projectId: string;
  data: SpecificationsLoaderData;
  readOnly?: boolean;
}

export const SpecificationsView = ({
  projectId,
  data,
  readOnly = false,
}: SpecificationsViewProps) => {
  const layoutData = useRouteLoaderData(
    WORKSPACE_LOADER_ID,
  ) as WorkspaceLoaderData;
  const revalidator = useRevalidator();
  const fetcher = useFetcher<SpecificationsActionResult>();
  const runFetcher = useFetcher<SpecificationsActionResult>();
  const deleteFetcher = useFetcher<SpecificationsActionResult>();
  const { toast } = useToast();
  const lastHandledResponse = useRef<string | null>(null);
  const lastHandledRunResponse = useRef<string | null>(null);
  const lastHandledDeleteResponse = useRef<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [primaryDatasetId, setPrimaryDatasetId] = useState<string | null>(null);
  const [evalType, setEvalType] = useState<
    "Strict Equality" | "Any permitted" | "LLM-As-A-Judge"
  >("Strict Equality");
  const [permittedValues, setPermittedValues] = useState("");
  const [judgeId, setJudgeId] = useState<string | null>(null);
  const [attackMethods, setAttackMethods] = useState<AttackMethod[]>([]);
  const [selectedMethods, setSelectedMethods] = useState<string[]>([]);
  const [methodsLoading, setMethodsLoading] = useState(false);
  const [methodsError, setMethodsError] = useState<string | null>(null);
  const [expandedSpecs, setExpandedSpecs] = useState<Set<string>>(new Set());

  const primaryDatasets = useMemo(
    () => layoutData.datasets.filter((dataset) => dataset.type === "primary"),
    [layoutData.datasets],
  );

  const judgeMap = useMemo(() => {
    return new Map(layoutData.judges.map((judge) => [judge.id, judge.title]));
  }, [layoutData.judges]);

  useEffect(() => {
    if (!dialogOpen) return;

    const loadMethods = async () => {
      setMethodsLoading(true);
      setMethodsError(null);
      try {
        const methods = await getAttackMethods();
        setAttackMethods(methods);
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Failed to load attack methods";
        setMethodsError(message);
      } finally {
        setMethodsLoading(false);
      }
    };

    loadMethods();
  }, [dialogOpen]);

  useEffect(() => {
    if (!dialogOpen) return;

    if (!primaryDatasetId && primaryDatasets.length > 0) {
      setPrimaryDatasetId(primaryDatasets[0]?.id ?? null);
    }
  }, [dialogOpen, primaryDatasetId, primaryDatasets]);

  useEffect(() => {
    if (fetcher.state !== "idle" || !fetcher.data) {
      return;
    }

    const responseKey = fetcher.data.ok
      ? `ok:${fetcher.data.specificationId}`
      : `error:${fetcher.data.error ?? "unknown"}`;

    if (lastHandledResponse.current === responseKey) {
      return;
    }

    lastHandledResponse.current = responseKey;

    if (fetcher.data.ok) {
      toast({
        title: "Specification created",
        description: "Adversarial datasets are being generated.",
      });
      // Auto-expand newly created specification
      const newSpecId = fetcher.data.specificationId;
      if (newSpecId) {
        setExpandedSpecs((prev) => new Set([...prev, newSpecId]));
      }
      setDialogOpen(false);
      setName("");
      setDescription("");
      setPrimaryDatasetId(null);
      setEvalType("Strict Equality");
      setPermittedValues("");
      setJudgeId(null);
      setSelectedMethods([]);
      revalidator.revalidate();
      return;
    }

    toast({
      title: "Error",
      description: fetcher.data.error,
      variant: "destructive",
    });
  }, [fetcher.state, fetcher.data, toast, revalidator]);

  useEffect(() => {
    if (runFetcher.state !== "idle" || !runFetcher.data) {
      return;
    }

    const responseKey = runFetcher.data.ok
      ? runFetcher.data.intent === "specificationRun"
        ? `ok:${runFetcher.data.specificationId}:${runFetcher.data.pgflowRunId}`
        : `ok:${runFetcher.data.specificationId}`
      : `error:${runFetcher.data.error ?? "unknown"}`;

    if (lastHandledRunResponse.current === responseKey) {
      return;
    }

    lastHandledRunResponse.current = responseKey;

    if (runFetcher.data.ok && runFetcher.data.intent === "specificationRun") {
      toast({
        title: "Specification run started",
        description: "Test runs will appear shortly in Results.",
      });
      revalidator.revalidate();
      return;
    }

    if (!runFetcher.data.ok) {
      toast({
        title: "Error",
        description: runFetcher.data.error,
        variant: "destructive",
      });
    }
  }, [runFetcher.state, runFetcher.data, toast, revalidator]);

  useEffect(() => {
    if (deleteFetcher.state !== "idle" || !deleteFetcher.data) {
      return;
    }

    const responseKey = deleteFetcher.data.ok
      ? `ok:${deleteFetcher.data.specificationId}`
      : `error:${deleteFetcher.data.error ?? "unknown"}`;

    if (lastHandledDeleteResponse.current === responseKey) {
      return;
    }

    lastHandledDeleteResponse.current = responseKey;

    if (deleteFetcher.data.ok) {
      toast({
        title: "Specification deleted",
        description: "The specification and its associated data have been removed.",
      });
      revalidator.revalidate();
      return;
    }

    toast({
      title: "Error",
      description: deleteFetcher.data.error,
      variant: "destructive",
    });
  }, [deleteFetcher.state, deleteFetcher.data, toast, revalidator]);

  const toggleMethod = (value: string) => {
    setSelectedMethods((prev) =>
      prev.includes(value)
        ? prev.filter((method) => method !== value)
        : [...prev, value],
    );
  };

  const toggleSpec = (specId: string) => {
    setExpandedSpecs((prev) => {
      const next = new Set(prev);
      if (next.has(specId)) {
        next.delete(specId);
      } else {
        next.add(specId);
      }
      return next;
    });
  };

  const isExpanded = (specId: string) => expandedSpecs.has(specId);

  const handleSubmit = () => {
    if (readOnly) {
      toast({
        title: "Read-only",
        description: "You don't have permission to create specifications here.",
        variant: "destructive",
      });
      return;
    }

    if (!name.trim()) {
      toast({
        title: "Missing name",
        description: "Please enter a specification name.",
        variant: "destructive",
      });
      return;
    }

    if (!primaryDatasetId) {
      toast({
        title: "Missing dataset",
        description: "Select a primary dataset for this specification.",
        variant: "destructive",
      });
      return;
    }

    if (selectedMethods.length === 0) {
      toast({
        title: "Missing methods",
        description: "Select at least one attack method.",
        variant: "destructive",
      });
      return;
    }

    if (evalType === "Any permitted" && !permittedValues.trim()) {
      toast({
        title: "Missing permitted values",
        description: "Enter permitted values for this evaluation type.",
        variant: "destructive",
      });
      return;
    }

    if (evalType === "LLM-As-A-Judge" && !judgeId) {
      toast({
        title: "Missing judge",
        description: "Select a judge for LLM-as-a-Judge evaluation.",
        variant: "destructive",
      });
      return;
    }

    const formData = new FormData();
    formData.set("intent", "specificationCreate");
    formData.set("projectId", projectId);
    formData.set("name", name.trim());
    formData.set("primaryDatasetId", primaryDatasetId);
    formData.set("evalType", evalType);
    formData.set("attackMethods", JSON.stringify(selectedMethods));

    if (description.trim()) {
      formData.set("description", description.trim());
    }

    if (evalType === "Any permitted") {
      formData.set("permittedValues", permittedValues.trim());
    }

    if (evalType === "LLM-As-A-Judge" && judgeId) {
      formData.set("judgeId", judgeId);
    }

    fetcher.submit(formData, { method: "post" });
  };

  const handleRunSpecification = (specificationId: string) => {
    if (readOnly) {
      toast({
        title: "Read-only",
        description: "You don't have permission to run specifications here.",
        variant: "destructive",
      });
      return;
    }

    const formData = new FormData();
    formData.set("intent", "specificationRun");
    formData.set("specificationId", specificationId);
    runFetcher.submit(formData, { method: "post" });
  };

  const handleDeleteSpecification = (specificationId: string) => {
    if (readOnly) {
      toast({
        title: "Read-only",
        description: "You don't have permission to delete specifications here.",
        variant: "destructive",
      });
      return;
    }

    const formData = new FormData();
    formData.set("intent", "specificationDelete");
    formData.set("specificationId", specificationId);
    deleteFetcher.submit(formData, { method: "post" });
  };

  const specifications = useMemo(
    () => data.specifications ?? [],
    [data.specifications],
  );

  const hasRunningSpecRun = useMemo(
    () => specifications.some((spec) => spec.latest_run?.status === "Running"),
    [specifications],
  );

  useEffect(() => {
    if (!hasRunningSpecRun) return;

    const intervalId = setInterval(() => {
      revalidator.revalidate();
    }, 5000);

    return () => {
      clearInterval(intervalId);
    };
  }, [hasRunningSpecRun, revalidator]);

  const PAGE_SIZE = 5;
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(specifications.length / PAGE_SIZE);
  const paginatedSpecs = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return specifications.slice(start, start + PAGE_SIZE);
  }, [specifications, currentPage]);

  // Reset to page 1 when specifications change
  useEffect(() => {
    setCurrentPage(1);
  }, [specifications.length]);

  return (
    <div className="space-y-6" data-testid="specifications-view">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-foreground">
            Specifications
          </h2>
          <p className="text-sm text-muted-foreground">
            Define repeatable test suites and generate adversarial datasets for
            robustness checks.
          </p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          data-testid="create-specification-button"
          disabled={readOnly}
        >
          <Plus className="h-4 w-4 mr-2" />
          New Specification
        </Button>
      </div>

      {specifications.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-sm text-muted-foreground">
              No specifications yet. Create one to start tracking
              repeatable runs.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {paginatedSpecs.map((spec) => {
            const datasets = spec.specification_datasets ?? [];
            const primaryDataset = datasets.find(
              (dataset) => dataset.role === "primary",
            );
            const derivativeDatasets = datasets.filter(
              (dataset) => dataset.role === "adversarial",
            );
            const latestRun = spec.latest_run ?? null;
            const isRunStarting =
              runFetcher.state !== "idle" &&
              runFetcher.formData?.get("specificationId") === spec.id;
            const hasPrimaryDataset = Boolean(primaryDataset?.dataset_id);
            const hasGeneratingDerivatives = derivativeDatasets.some(
              (dataset) =>
                dataset.status === "generating" || dataset.status === "pending",
            );
            const hasInvalidDerivatives = derivativeDatasets.some(
              (dataset) => !dataset.parent_dataset_id || !dataset.attack_method,
            );
            const runDisabledReason = readOnly
              ? "This eval is read-only."
              : isRunStarting
                ? "Starting specification run..."
                : !hasPrimaryDataset
                  ? "Primary dataset is missing. Restore it to run this specification."
                  : hasGeneratingDerivatives
                    ? "Adversarial datasets are still generating. Wait for them to finish."
                    : hasInvalidDerivatives
                      ? "One or more adversarial datasets are missing their parent or attack method."
                      : null;
            const primaryName =
              primaryDataset?.test_data_sets?.name ?? "Unknown primary";
            const primaryDatasetId = primaryDataset?.dataset_id ?? null;
            const evalDetail =
              spec.eval_type === "LLM-As-A-Judge"
                ? `Judge: ${judgeMap.get(spec.judge_id ?? "") ?? "Unknown"}`
                : spec.eval_type === "Any permitted"
                  ? `Permitted: ${spec.permitted_values ?? "-"}`
                  : "Strict Equality";
            const latestRunTimestamp = formatRunTimestamp(latestRun);
            const latestRunProgress = formatRunProgress(latestRun);

            return (
              <Collapsible
                key={spec.id}
                open={isExpanded(spec.id)}
                onOpenChange={() => toggleSpec(spec.id)}
              >
                <Card data-testid={`specification-card-${spec.id}`}>
                  <CardHeader>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <CardTitle>{spec.name}</CardTitle>
                        {spec.description && (
                          <CardDescription>
                            {spec.description}
                          </CardDescription>
                        )}
                        {latestRun ? (
                          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                            <Badge variant="outline">
                              {formatRunStatus(latestRun.status)}
                            </Badge>
                            {latestRunProgress ? (
                              <span
                                className="text-xs text-muted-foreground"
                                data-testid={`spec-run-progress-${spec.id}`}
                              >
                                {latestRunProgress}
                              </span>
                            ) : null}
                            {latestRunTimestamp ? (
                              <span className="text-xs text-muted-foreground">
                                {latestRunTimestamp}
                              </span>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {spec.locked_at && (
                          <Badge variant="secondary" className="gap-1">
                            <Lock className="h-3 w-3" />
                            Locked
                          </Badge>
                        )}
                        {runDisabledReason ? (
                          <TooltipProvider delayDuration={0}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled
                                    className="gap-2"
                                    data-testid={`run-spec-${spec.id}`}
                                  >
                                    {isRunStarting ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <PlayCircle className="h-4 w-4" />
                                    )}
                                    Run spec
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>{runDisabledReason}</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-2"
                            data-testid={`run-spec-${spec.id}`}
                            onClick={() => handleRunSpecification(spec.id)}
                          >
                            <PlayCircle className="h-4 w-4" />
                            Run spec
                          </Button>
                        )}
                        {spec.locked_at ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled
                            className="text-muted-foreground"
                            data-testid={`delete-spec-${spec.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                            <span className="sr-only">Delete specification (locked)</span>
                          </Button>
                        ) : readOnly ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled
                            className="text-muted-foreground"
                            data-testid={`delete-spec-${spec.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                            <span className="sr-only">Delete specification</span>
                          </Button>
                        ) : (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                data-testid={`delete-spec-${spec.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                                <span className="sr-only">Delete specification</span>
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Specification</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete "{spec.name}"? This will also delete all associated adversarial datasets and test runs. This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteSpecification(spec.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <ChevronDown
                              className={cn(
                                "h-4 w-4 transition-transform duration-200",
                                isExpanded(spec.id) && "rotate-180"
                              )}
                            />
                            <span className="sr-only">Toggle details</span>
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                    </div>
                  </CardHeader>
                  <CollapsibleContent>
                    <CardContent className="space-y-4 pt-0">
                      <div className="grid gap-3 md:grid-cols-2">
                        <div>
                          <p className="text-xs text-muted-foreground">Primary</p>
                          {primaryDatasetId ? (
                            <Link
                              to={`/dataset/${primaryDatasetId}`}
                              className="text-sm font-medium hover:underline underline-offset-2"
                            >
                              {primaryName}
                            </Link>
                          ) : (
                            <p className="text-sm font-medium">{primaryName}</p>
                          )}
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Eval</p>
                          <p className="text-sm font-medium">
                            {evalDetail}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">Latest run</p>
                          {latestRun ? (
                            <div className="flex items-center gap-2 text-sm">
                              <Badge variant="outline">
                                {formatRunStatus(latestRun.status)}
                              </Badge>
                              {latestRunProgress ? (
                                <span className="text-xs text-muted-foreground">
                                  {latestRunProgress}
                                </span>
                              ) : null}
                              <span className="text-xs text-muted-foreground">
                                {latestRunTimestamp ?? "Just now"}
                              </span>
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground">
                              No runs yet
                            </p>
                          )}
                        </div>
                        {latestRun ? (
                          <Button asChild size="sm" variant="outline">
                            <Link
                              to={`/workspace/evals/${projectId}/results?specRunId=${latestRun.id}`}
                            >
                              View robustness
                            </Link>
                          </Button>
                        ) : null}
                      </div>

                      <div>
                        <p className="text-xs text-muted-foreground">
                          Adversarial
                        </p>
                        {derivativeDatasets.length === 0 ? (
                          <p className="text-sm text-muted-foreground">
                            No adversarial datasets configured.
                          </p>
                        ) : (
                          <div className="space-y-2 mt-2">
                            {derivativeDatasets.map((dataset) => {
                              const datasetName =
                                dataset.test_data_sets?.name ??
                                formatAttackMethodLabel(
                                  dataset.attack_method ?? "",
                                );
                              const datasetId = dataset.dataset_id ?? null;
                              const statusLabel = dataset.status.replace(
                                /^[a-z]/,
                                (match) => match.toUpperCase(),
                              );

                              return (
                                <div
                                  key={dataset.id}
                                  className="flex items-start justify-between gap-3 rounded-md border p-3"
                                >
                                  <div className="space-y-1">
                                    {datasetId ? (
                                      <Link
                                        to={`/dataset/${datasetId}`}
                                        className="text-sm font-medium hover:underline underline-offset-2"
                                      >
                                        {datasetName}
                                      </Link>
                                    ) : (
                                      <p className="text-sm font-medium">
                                        {datasetName}
                                      </p>
                                    )}
                                    {dataset.attack_method && (
                                      <p className="text-xs text-muted-foreground">
                                        Method: {formatAttackMethodLabel(dataset.attack_method)}
                                      </p>
                                    )}
                                    {dataset.test_data_sets?.dataset_kind && (
                                      <p className="text-xs text-muted-foreground">
                                        Kind: {getDatasetKindLabel(dataset.test_data_sets.dataset_kind)}
                                      </p>
                                    )}
                                    {dataset.status === "failed" && dataset.error && (
                                      <p className="text-xs text-destructive">
                                        {dataset.error}
                                      </p>
                                    )}
                                  </div>
                                  <Badge variant={getStatusBadgeVariant(dataset.status)}>
                                    {statusLabel}
                                  </Badge>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            );
          })}

          {totalPages > 1 && (
            <Pagination className="mt-6">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentPage((p) => Math.max(1, p - 1));
                    }}
                    className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <PaginationItem key={page}>
                    <PaginationLink
                      href="#"
                      isActive={page === currentPage}
                      onClick={(e) => {
                        e.preventDefault();
                        setCurrentPage(page);
                      }}
                      className="cursor-pointer"
                    >
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentPage((p) => Math.min(totalPages, p + 1));
                    }}
                    className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create specification</DialogTitle>
            <DialogDescription>
              Choose a primary dataset, attack methods, and evaluation rules.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="spec-name">Name</Label>
              <Input
                id="spec-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Robustness baseline"
                data-testid="specification-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="spec-description">Description</Label>
              <Textarea
                id="spec-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Optional notes about this specification"
                data-testid="specification-description-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Primary dataset</Label>
              <Select
                value={primaryDatasetId ?? ""}
                onValueChange={(value) =>
                  setPrimaryDatasetId(value || null)
                }
              >
                <SelectTrigger data-testid="specification-primary-select">
                  <SelectValue placeholder="Select a dataset" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Behavior datasets</SelectLabel>
                    {primaryDatasets
                      .filter(
                        (dataset) =>
                          dataset.dataset_kind === "behavior_examples",
                      )
                      .map((dataset) => (
                        <SelectItem key={dataset.id} value={dataset.id}>
                          {dataset.name}
                          {dataset.is_public ? " (Public)" : ""}
                        </SelectItem>
                      ))}
                  </SelectGroup>
                  <SelectGroup>
                    <SelectLabel>Rules datasets</SelectLabel>
                    {primaryDatasets
                      .filter(
                        (dataset) =>
                          dataset.dataset_kind === "operational_rules",
                      )
                      .map((dataset) => (
                        <SelectItem key={dataset.id} value={dataset.id}>
                          {dataset.name}
                          {dataset.is_public ? " (Public)" : ""}
                        </SelectItem>
                      ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Evaluation</Label>
              <Select
                value={evalType}
                onValueChange={(value) =>
                  setEvalType(value as typeof evalType)
                }
              >
                <SelectTrigger data-testid="specification-eval-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Strict Equality">
                    Strict Equality
                  </SelectItem>
                  <SelectItem value="Any permitted">
                    Permitted Values
                  </SelectItem>
                  <SelectItem value="LLM-As-A-Judge">LLM Judge</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {evalType === "Any permitted" && (
              <div className="space-y-2">
                <Label>Permitted values</Label>
                <Input
                  value={permittedValues}
                  onChange={(event) =>
                    setPermittedValues(event.target.value)
                  }
                  placeholder="true, false, yes"
                  data-testid="specification-permitted-values"
                />
              </div>
            )}

            {evalType === "LLM-As-A-Judge" && (
              <div className="space-y-2">
                <Label>Judge</Label>
                <Select
                  value={judgeId ?? ""}
                  onValueChange={(value) =>
                    setJudgeId(value === "none" ? null : value)
                  }
                >
                  <SelectTrigger data-testid="specification-judge-select">
                    <SelectValue placeholder="Select a judge" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      No judge selected
                    </SelectItem>
                    {layoutData.judges.map((judge) => (
                      <SelectItem key={judge.id} value={judge.id}>
                        {judge.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Attack methods</Label>
              {methodsError && (
                <Alert variant="destructive">
                  <AlertDescription>{methodsError}</AlertDescription>
                </Alert>
              )}
              {methodsLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading attack methods...
                </div>
              ) : (
                <div className="max-h-48 overflow-y-auto rounded-md border p-3 space-y-2">
                  {attackMethods.map((method) => (
                    <label
                      key={method.value}
                      className="flex items-center gap-2 text-sm"
                    >
                      <Checkbox
                        checked={selectedMethods.includes(method.value)}
                        onCheckedChange={() => toggleMethod(method.value)}
                        data-testid={`specification-method-${method.value}`}
                      />
                      <span>{method.label}</span>
                    </label>
                  ))}
                  {attackMethods.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      No attack methods available.
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setDialogOpen(false)}
                disabled={fetcher.state !== "idle"}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={fetcher.state !== "idle" || methodsLoading}
                data-testid="specification-submit"
              >
                {fetcher.state !== "idle" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Create specification"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
